export type PlanTierKey = "trial" | "starter" | "growth" | "professional" | "business" | "enterprise";

export type EntitlementFeatureKey =
  | "basic_pipeline"
  | "whatsapp_email_automation"
  | "round_robin"
  | "team_management"
  | "advanced_analytics"
  | "sow_generator"
  | "audit_trail"
  | "multi_channel_broadcast"
  | "multi_branch"
  | "custom_erp_sync"
  | "high_availability_failover"
  | "autodialer";

export type AddonKey =
  | "onboarding_standard"
  | "onboarding_turnkey_vip"
  | "payment_gateway_integration"
  | "erp_tally_sync"
  | "custom_workflows_fields"
  | "custom_contract_templates"
  | "legacy_data_migration"
  | "autodialer_module";

export interface PlanDefinition {
  id: PlanTierKey;
  name: string;
  tagline: string;
  popular?: boolean;
  badge?: string;
  maxSeats: number; // 0 = unlimited
  seatsBadge: string;
  pricing: {
    INR: { monthly: number; setup: number };
    USD: { monthly: number; setup: number };
    AED: { monthly: number; setup: number };
  };
  features: EntitlementFeatureKey[];
  displayFeatures: string[];
  missingFeatures: string[];
  includedQuotas: {
    whatsapp: number;
    sms: number;
    callMinutes: number;
  };
  monthlyLeadLimit: number; // 0 = unlimited
}

export interface AddonDefinition {
  id: AddonKey;
  category: "implementation" | "integration" | "customization" | "migration" | "telephony";
  name: string;
  description: string;
  pricing: {
    INR: { setup: number; monthly: number };
    USD: { setup: number; monthly: number };
    AED: { setup: number; monthly: number };
  };
  isRecurring: boolean;
}

export interface UsageRates {
  whatsapp: {
    utilityINR: number;
    marketingINR: number;
    utilityUSD: number;
    marketingUSD: number;
    utilityAED: number;
    marketingAED: number;
    unit: string;
  };
  sms: {
    domesticINR: number;
    internationalUSD: number;
    internationalAED: number;
    unit: string;
  };
  autodialerVoice: {
    ratePerMinINR: number;
    ratePerMinUSD: number;
    ratePerMinAED: number;
    unit: string;
  };
  aiTokens: {
    ratePer1kINR: number;
    ratePer1kUSD: number;
    ratePer1kAED: number;
    unit: string;
  };
}

export const USAGE_RATES: UsageRates = {
  whatsapp: {
    utilityINR: 0.40,
    marketingINR: 0.88,
    utilityUSD: 0.008,
    marketingUSD: 0.016,
    utilityAED: 0.030,
    marketingAED: 0.060,
    unit: "per delivered 24h Meta conversation window (wholesale pass-through)",
  },
  sms: {
    domesticINR: 0.18,
    internationalUSD: 0.035,
    internationalAED: 0.12,
    unit: "per 160-character segment (carrier direct)",
  },
  autodialerVoice: {
    ratePerMinINR: 0.95,
    ratePerMinUSD: 0.025,
    ratePerMinAED: 0.08,
    unit: "per connected call minute",
  },
  aiTokens: {
    ratePer1kINR: 0.12,
    ratePer1kUSD: 0.002,
    ratePer1kAED: 0.007,
    unit: "per 1,000 AI tokens for dynamic qualification & scoring",
  },
};

export const PLANS: Record<PlanTierKey, PlanDefinition> = {
  trial: {
    id: "trial",
    name: "7-Day Free Evaluation",
    tagline: "Explore Dynamic Multi-Industry CRM & Pipeline Capabilities",
    maxSeats: 1,
    seatsBadge: "1 User Login",
    pricing: {
      INR: { monthly: 0, setup: 0 },
      USD: { monthly: 0, setup: 0 },
      AED: { monthly: 0, setup: 0 },
    },
    features: [
      "basic_pipeline",
      "whatsapp_email_automation",
      "round_robin",
      "team_management",
      "advanced_analytics",
      "sow_generator",
      "audit_trail",
      "multi_channel_broadcast",
      "multi_branch",
      "custom_erp_sync",
      "high_availability_failover",
      "autodialer",
    ],
    displayFeatures: [
      "Full Unrestricted Access to All Platform Features (7-Day Trial)",
      "Dynamic Multi-Industry Preset Engine (Trading, Real Estate, Healthcare, etc.)",
      "Dynamic Custom Fields & Custom Column Schema Support",
      "Smart Bulk CSV / Excel Import (Auto-matches your business headers)",
      "5-Stage Visual Kanban Board with Stage Customizer",
      "Team Management, SOW Generator & Multi-Channel Pipeline Access",
      "Evaluation Communication Sandbox (100 WA / 25 SMS / 10 Call Mins)",
      "Zero Credit Card Required — Instant Verification",
    ],
    missingFeatures: [],
    includedQuotas: { whatsapp: 100, sms: 25, callMinutes: 10 },
    monthlyLeadLimit: 5000,
  },

  starter: {
    id: "starter",
    name: "Starter",
    tagline: "For Solo Professionals, Small Brokers & Growing Sales Teams",
    maxSeats: 2,
    seatsBadge: "Up to 2 Users",
    pricing: {
      INR: { monthly: 2499, setup: 9999 },
      USD: { monthly: 35, setup: 149 },
      AED: { monthly: 130, setup: 490 },
    },
    features: ["basic_pipeline"],
    displayFeatures: [
      "Up to 2 User Logins Included (Zero Per-Seat Taxes)",
      "Unified CRM Dashboard & Pipeline View",
      "Dynamic Custom Fields & Schema Customizer",
      "Lead Management & Deal Value Tracking (Multi-Currency)",
      "Smart CSV Import & Instant 50k Record Export",
      "Basic Customer Profile & Lead Activity History",
      "WhatsApp & SMS Communication History Tracking",
      "Standard Email Support (1 Business Day Response SLA)",
      "Automated Cloud Backup & Responsive Web Interface",
    ],
    missingFeatures: [
      "WhatsApp Cloud API Integration",
      "AI Automation & Pitch Generation",
      "Advanced Role-Based Permissions",
      "Automated Round-Robin Lead Assignment",
      "Bulk Marketing Campaigns",
    ],
    includedQuotas: { whatsapp: 250, sms: 100, callMinutes: 30 },
    monthlyLeadLimit: 1000,
  },

  growth: {
    id: "growth",
    name: "Growth",
    tagline: "Automate your sales process without building an enterprise system.",
    maxSeats: 5,
    seatsBadge: "Up to 5 Users",
    pricing: {
      INR: { monthly: 6999, setup: 19999 },
      USD: { monthly: 89, setup: 279 },
      AED: { monthly: 330, setup: 990 },
    },
    features: [
      "basic_pipeline",
      "whatsapp_email_automation",
      "team_management",
    ],
    displayFeatures: [
      "Up to 5 Users / Telecallers Included (Zero Per-Seat Taxes)",
      "Official Meta WhatsApp Cloud API Integration & Verification Assistance",
      "Sub-15s Speed-to-Lead Automation & Automated First Response",
      "Interactive WhatsApp Live Chat Drawer with Human Takeover",
      "Lead Assignment Rules & Team Member Management",
      "Basic Role Permissions & Granular Access",
      "Lead Source Tracking & UTM Conversion Analytics",
      "Multi-Industry AI Sales Pitch Generation (WhatsApp & Email)",
      "Custom Excel Format Ingestion & Follow-Up Reminders",
      "Priority Support (4 Business Hours Response SLA)",
      "WhatsApp Cloud API Included (Third-party messaging charges billed separately)",
    ],
    missingFeatures: [
      "Automated Round-Robin Lead Distribution",
      "1-Click Proposal, SOW & Contract Generator",
      "Full Chronological Telecaller Audit Trail",
      "Multi-Branch & Team Hierarchy",
    ],
    includedQuotas: { whatsapp: 1000, sms: 500, callMinutes: 100 },
    monthlyLeadLimit: 5000,
  },

  professional: {
    id: "professional",
    name: "Professional",
    tagline: "Advanced CRM Automation for Growing Sales Teams",
    popular: true,
    badge: "MOST POPULAR",
    maxSeats: 10,
    seatsBadge: "Up to 10 Users",
    pricing: {
      INR: { monthly: 14999, setup: 39999 },
      USD: { monthly: 199, setup: 549 },
      AED: { monthly: 750, setup: 1950 },
    },
    features: [
      "basic_pipeline",
      "whatsapp_email_automation",
      "team_management",
      "round_robin",
      "advanced_analytics",
    ],
    displayFeatures: [
      "Up to 10 Users / Telecallers Included (Zero Per-Seat Taxes)",
      "Automated Round-Robin Lead Routing across Sales Agents",
      "Advanced KPI Dashboards, Conversion Funnels & RM Performance Reports",
      "Granular Role Hierarchy (Admin, Manager, Sales Agent)",
      "Multi-Stage Sales Pipelines & Deal Progression Analytics",
      "AI-Powered Outreach Engine & Pitch Customization",
      "WhatsApp Cloud API & SMS Gateway Integration",
      "Lead Deduplication Engine & Automated Merge Rules",
      "Bulk Import up to 15,000 Records with Smart Field Mapping",
      "Advanced Follow-Up Automation, Reminders & Lead Aging Reports",
      "Priority Technical Support (4-Hour Support Response SLA)",
      "Third-Party Messaging & Carrier Charges Billed Separately",
    ],
    missingFeatures: [
      "Multi-Branch Data Walls & Branch-Wise Dashboards",
      "Dedicated Account Manager",
      "Custom Legacy ERP & Tally Integration",
    ],
    includedQuotas: { whatsapp: 3000, sms: 1500, callMinutes: 300 },
    monthlyLeadLimit: 15000,
  },

  business: {
    id: "business",
    name: "Business",
    tagline: "Advanced CRM Infrastructure for Larger Sales Operations",
    maxSeats: 25,
    seatsBadge: "Up to 25 Users",
    pricing: {
      INR: { monthly: 29999, setup: 75000 },
      USD: { monthly: 399, setup: 999 },
      AED: { monthly: 1450, setup: 3650 },
    },
    features: [
      "basic_pipeline",
      "whatsapp_email_automation",
      "team_management",
      "round_robin",
      "advanced_analytics",
      "sow_generator",
      "audit_trail",
      "multi_channel_broadcast",
      "high_availability_failover",
    ],
    displayFeatures: [
      "Up to 25 Users / Telecallers Included (Zero Per-Seat Taxes)",
      "Advanced Multi-Team Management & Multiple Sales Pipelines",
      "Branch & Team Hierarchy with Advanced Management Dashboards",
      "Full Chronological Audit Trail & Complete User Activity History",
      "Advanced MIS Reports & Broker Commission Tracking",
      "1-Click Client Agreement, SOW & Commercial Invoice PDF Generator",
      "Bulk Import up to 50,000 Records & Instant Data Exports",
      "Advanced API Integrations & Custom Workflow Automation",
      "Multiple WhatsApp Numbers & Advanced WhatsApp Automation",
      "Dedicated Onboarding & Dedicated Account Manager",
      "Monthly Business Review & 2-Hour Priority Support Response SLA",
      "Custom Development & Integrations Separately Estimated",
    ],
    missingFeatures: [
      "Custom Bare-Metal Cluster & Custom SLA",
    ],
    includedQuotas: { whatsapp: 7500, sms: 3500, callMinutes: 750 },
    monthlyLeadLimit: 50000,
  },

  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Custom Architecture based on Users, Branches, Integrations & SLA",
    maxSeats: 0, // unlimited
    seatsBadge: "Large-Scale User Deployment",
    pricing: {
      INR: { monthly: 49999, setup: 150000 },
      USD: { monthly: 649, setup: 1999 },
      AED: { monthly: 2450, setup: 7200 },
    },
    features: [
      "basic_pipeline",
      "whatsapp_email_automation",
      "team_management",
      "round_robin",
      "advanced_analytics",
      "sow_generator",
      "audit_trail",
      "multi_channel_broadcast",
      "high_availability_failover",
      "multi_branch",
      "custom_erp_sync",
    ],
    displayFeatures: [
      "Large-Scale User Deployment with Tailored Limits",
      "Multi-Branch Architecture with Branch-Wise Dedicated Dashboards",
      "Advanced Role Hierarchy & Bespoke CRM Workflows",
      "Multiple WhatsApp Cloud API Numbers & Enterprise Routing",
      "Custom Legacy ERP, Tally & Core Banking/Financial API Integrations",
      "Custom Reporting, Advanced Audit Logs & Compliance Controls",
      "Dedicated CRM Cloud Environment where Required",
      "Custom PWA/Mobile Experience & Legacy Data Migration",
      "Dedicated Onboarding, Admin Training & Enterprise SLA",
      "Dedicated Account Management & Technical Escalation",
      "Enterprise Backup & Disaster Recovery Architecture",
      "White-Label & Custom Development Options",
    ],
    missingFeatures: [],
    includedQuotas: { whatsapp: 25000, sms: 10000, callMinutes: 2500 },
    monthlyLeadLimit: 0, // unlimited
  },
};

export const ADDONS: Record<AddonKey, AddonDefinition> = {
  onboarding_standard: {
    id: "onboarding_standard",
    category: "implementation",
    name: "Standard Rapid Onboarding",
    description: "Assisted setup of pipelines, user provisioning, custom branding, and WhatsApp number verification within 48 hours.",
    pricing: {
      INR: { setup: 9999, monthly: 0 },
      USD: { setup: 149, monthly: 0 },
      AED: { setup: 490, monthly: 0 },
    },
    isRecurring: false,
  },
  onboarding_turnkey_vip: {
    id: "onboarding_turnkey_vip",
    category: "implementation",
    name: "VIP Turnkey Implementation",
    description: "End-to-end white-glove setup: Meta business manager verification, custom domain SSL, telecaller staff training, and live pipeline rehearsal.",
    pricing: {
      INR: { setup: 24999, monthly: 0 },
      USD: { setup: 349, monthly: 0 },
      AED: { setup: 1200, monthly: 0 },
    },
    isRecurring: false,
  },
  payment_gateway_integration: {
    id: "payment_gateway_integration",
    category: "integration",
    name: "Payment Gateway Checkout Integration",
    description: "Razorpay, Cashfree, or Stripe checkout webhooks with automated payment link generation, receipt dispatch, and CRM deal auto-closing.",
    pricing: {
      INR: { setup: 19999, monthly: 0 },
      USD: { setup: 299, monthly: 0 },
      AED: { setup: 990, monthly: 0 },
    },
    isRecurring: false,
  },
  erp_tally_sync: {
    id: "erp_tally_sync",
    category: "integration",
    name: "ERP & Accounting Webhook Synchronization",
    description: "Bi-directional ledger and customer balance synchronization with Tally Prime, Zoho Books, SAP, or QuickBooks.",
    pricing: {
      INR: { setup: 0, monthly: 4500 },
      USD: { setup: 0, monthly: 75 },
      AED: { setup: 0, monthly: 275 },
    },
    isRecurring: true,
  },
  custom_workflows_fields: {
    id: "custom_workflows_fields",
    category: "customization",
    name: "Bespoke Fields & Pipeline Customization",
    description: "Custom database schema modeling, specialized stage validation triggers, custom tags, and tailored data export formats.",
    pricing: {
      INR: { setup: 15000, monthly: 0 },
      USD: { setup: 220, monthly: 0 },
      AED: { setup: 750, monthly: 0 },
    },
    isRecurring: false,
  },
  custom_contract_templates: {
    id: "custom_contract_templates",
    category: "customization",
    name: "Custom SOW & Legal Contract Template",
    description: "Dynamic PDF contract generation with custom clauses, digital stamps, authorized signatory blocks, and instant WhatsApp dispatch.",
    pricing: {
      INR: { setup: 19999, monthly: 0 },
      USD: { setup: 299, monthly: 0 },
      AED: { setup: 990, monthly: 0 },
    },
    isRecurring: false,
  },
  legacy_data_migration: {
    id: "legacy_data_migration",
    category: "migration",
    name: "Legacy CRM & CSV Data Cleansing & Import",
    description: "Data de-duplication, format normalization, phone sanitation (E.164), and bulk ingestion of up to 25,000 historic customer records from Zoho, Salesforce, or Excel.",
    pricing: {
      INR: { setup: 14999, monthly: 0 },
      USD: { setup: 220, monthly: 0 },
      AED: { setup: 750, monthly: 0 },
    },
    isRecurring: false,
  },
  autodialer_module: {
    id: "autodialer_module",
    category: "telephony",
    name: "Outbound Power Auto-Dialer Engine",
    description: "Automated click-to-call campaign manager, agent call disposition logging, call recording storage, and speed-to-lead queue dispatcher.",
    pricing: {
      INR: { setup: 0, monthly: 9999 },
      USD: { setup: 0, monthly: 140 },
      AED: { setup: 0, monthly: 490 },
    },
    isRecurring: true,
  },
};

export interface DecisionTreeInputs {
  teamSize: "1-2" | "3-5" | "6-10" | "11-20" | "20+";
  channels: Array<"whatsapp" | "sms" | "email" | "autodialer">;
  monthlyLeadVolume: "<500" | "500-2500" | "2500-10000" | "10000+";
  integrations: Array<"standard" | "payment_gateway" | "erp_tally" | "custom_api">;
  migrationNeeds: "none" | "csv_standard" | "full_crm_migration";
  currency?: "INR" | "USD" | "AED";
}

export interface DecisionTreeResult {
  recommendedPlan: PlanDefinition;
  currency: "INR" | "USD" | "AED";
  currencySymbol: string;
  monthlySoftwareFee: number;
  recommendedAddons: AddonDefinition[];
  implementationFee: number;
  estimatedMonthlyUsage: {
    min: number;
    max: number;
    description: string;
  };
  totalUpfrontFirstMonth: {
    min: number;
    max: number;
  };
  rationales: string[];
}

export function calculateDecisionTreeEstimate(inputs: DecisionTreeInputs): DecisionTreeResult {
  const curr = inputs.currency || "INR";
  const sym = curr === "INR" ? "₹" : curr === "AED" ? "AED " : "$";

  let recommendedTier: PlanTierKey = "starter";
  const rationales: string[] = [];

  // Step 1: Team Size Rule
  if (inputs.teamSize === "1-2") {
    recommendedTier = "starter";
    rationales.push("Starter accommodates 1-2 sales reps with zero per-seat SaaS tax.");
  } else if (inputs.teamSize === "3-5") {
    recommendedTier = "growth";
    rationales.push("Growth plan covers up to 5 telecallers with automated WhatsApp & email outreach.");
  } else if (inputs.teamSize === "6-10") {
    recommendedTier = "professional";
    rationales.push("Professional plan provides 10 seats + automated round-robin distribution to prevent lead cherry-picking.");
  } else if (inputs.teamSize === "11-20") {
    recommendedTier = "business";
    rationales.push("Business plan provides 20 seats, chronological telecaller audit trail & instant client SOW/contract generator.");
  } else {
    recommendedTier = "enterprise";
    rationales.push("Enterprise accommodates 20+ unlimited team members with multi-branch isolation.");
  }

  // Step 2: Channel Checks (e.g. WhatsApp autonomous or Auto-Dialer pushes up tier or adds add-on)
  const recommendedAddons: AddonDefinition[] = [];

  if (inputs.channels.includes("whatsapp") && recommendedTier === "starter") {
    recommendedTier = "growth";
    rationales.push("Official Meta WhatsApp speed-to-lead automation requires Growth plan or higher.");
  }

  if (inputs.channels.includes("autodialer")) {
    recommendedAddons.push(ADDONS.autodialer_module);
    rationales.push("Outbound Power Auto-Dialer engine added for high-velocity outbound calling.");
  }

  // Step 3: Integrations
  if (inputs.integrations.includes("payment_gateway")) {
    recommendedAddons.push(ADDONS.payment_gateway_integration);
    rationales.push("Turnkey Payment Gateway checkout webhook added for automatic invoice dispatch.");
  }

  if (inputs.integrations.includes("erp_tally")) {
    if (recommendedTier !== "enterprise") {
      recommendedAddons.push(ADDONS.erp_tally_sync);
      rationales.push("ERP & Tally Prime 2-way sync add-on selected.");
    }
  }

  // Step 4: Migration Needs
  if (inputs.migrationNeeds === "csv_standard" || inputs.migrationNeeds === "full_crm_migration") {
    recommendedAddons.push(ADDONS.legacy_data_migration);
    rationales.push("Legacy CRM cleansing, deduplication and historical import service included.");
  }

  // Default Onboarding
  if (recommendedTier === "starter" || recommendedTier === "growth") {
    recommendedAddons.push(ADDONS.onboarding_standard);
  } else {
    recommendedAddons.push(ADDONS.onboarding_turnkey_vip);
  }

  const plan = PLANS[recommendedTier];
  const monthlySoftwareFee = plan.pricing[curr].monthly;
  const implementationFee = plan.pricing[curr].setup + recommendedAddons.reduce((sum, a) => sum + a.pricing[curr].setup, 0);
  const recurringAddonsFee = recommendedAddons.reduce((sum, a) => (a.isRecurring ? sum + a.pricing[curr].monthly : sum), 0);

  // Usage Estimate based on monthly lead volume
  let usageMin = 0;
  let usageMax = 0;
  if (inputs.monthlyLeadVolume === "<500") {
    usageMin = curr === "INR" ? 300 : curr === "AED" ? 15 : 4;
    usageMax = curr === "INR" ? 800 : curr === "AED" ? 35 : 10;
  } else if (inputs.monthlyLeadVolume === "500-2500") {
    usageMin = curr === "INR" ? 800 : curr === "AED" ? 40 : 10;
    usageMax = curr === "INR" ? 2500 : curr === "AED" ? 110 : 30;
  } else if (inputs.monthlyLeadVolume === "2500-10000") {
    usageMin = curr === "INR" ? 2500 : curr === "AED" ? 110 : 30;
    usageMax = curr === "INR" ? 8000 : curr === "AED" ? 350 : 95;
  } else {
    usageMin = curr === "INR" ? 8000 : curr === "AED" ? 350 : 95;
    usageMax = curr === "INR" ? 22000 : curr === "AED" ? 950 : 260;
  }

  const totalMonthlyMin = monthlySoftwareFee + recurringAddonsFee + usageMin;
  const totalMonthlyMax = monthlySoftwareFee + recurringAddonsFee + usageMax;

  return {
    recommendedPlan: plan,
    currency: curr,
    currencySymbol: sym,
    monthlySoftwareFee: monthlySoftwareFee + recurringAddonsFee,
    recommendedAddons,
    implementationFee,
    estimatedMonthlyUsage: {
      min: usageMin,
      max: usageMax,
      description: "Direct carrier wholesale pass-through for Meta WhatsApp 24h conversation windows & SMS dispatches.",
    },
    totalUpfrontFirstMonth: {
      min: implementationFee + totalMonthlyMin,
      max: implementationFee + totalMonthlyMax,
    },
    rationales,
  };
}
